import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import BudgetExtractorModal from "./BudgetExtractorModal";
import BudgetGrid from "../BudgetGrid/BudgetGrid";
import { deleteBudgets, extractBudget } from "../../redux";
import { createBudgetItem } from "../../redux";
import { Button } from "../Button/Button";
import { useDropzone } from "react-dropzone";
import {
  parseFile,
  ParsedFileData,
  getExcelSheetNames,
} from "../../utils/helpers/fileParser";
import "./BudgetExtractor.css";

// Step indicator component
interface StepIndicatorProps {
  currentStep:
    | "selectFile"
    | "selectSheet"
    | "detectBudget"
    | "updateGrid"
    | "saveBudget";
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep }) => {
  const steps = [
    { key: "selectFile", number: 1, shortLabel: "select file" },
    { key: "selectSheet", number: 2, shortLabel: "select sheet" },
    { key: "detectBudget", number: 3, shortLabel: "detect budget" },
    { key: "updateGrid", number: 4, shortLabel: "update grid" },
    { key: "saveBudget", number: 5, shortLabel: "save budget" },
  ];

  const getCurrentStepIndex = () => {
    return steps.findIndex((step) => step.key === currentStep);
  };

  const currentStepIndex = getCurrentStepIndex();

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0px 20px",
        backgroundColor: "#fafafa",
        gap: "20px",
        width: "100%",
        marginLeft: "-30px",
      }}
    >
      {steps.map((step, index) => {
        const isActive = index === currentStepIndex;
        const isCompleted = index < currentStepIndex;

        return (
          <React.Fragment key={step.key}>
            {index > 0 && (
              <div
                style={{
                  width: "40px",
                  height: "2px",
                  backgroundColor: isCompleted ? "#228B22" : "#e0e0e0",
                  transition: "background-color 0.3s ease",
                }}
              />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                minWidth: "100px",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  backgroundColor: isCompleted
                    ? "#228B22"
                    : isActive
                    ? "#2196f3"
                    : "#e0e0e0",
                  color: isCompleted || isActive ? "#fff" : "#999",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "16px",
                  transition: "all 0.3s ease",
                  border: isActive ? "3px solid #1976d2" : "none",
                }}
              >
                {isCompleted ? "✓" : step.number}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: isActive ? "bold" : "normal",
                  color: isCompleted
                    ? "#228B22"
                    : isActive
                    ? "#2196f3"
                    : "#999",
                  textAlign: "center",
                  transition: "all 0.3s ease",
                }}
              >
                {step.shortLabel}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

interface BudgetExtractorProps {
  open: boolean;
  onClose: () => void;
  projectUuid: string;
  companyId: string;
  onBudgetSaved: () => void;
}

interface ExtractedData {
  project_uuid: string;
  project_name: string;
  status: string;
  budget_items: Array<{
    row: string;
    description: string;
    cost: string;
  }>;
  total_items: number;
  description_column: string;
  cost_column: string;
  error: string | null;
  excel_data: {
    data: any[][];
    columns: string[];
  };
}

export const BudgetExtractor: React.FC<BudgetExtractorProps> = ({
  open,
  onClose,
  projectUuid,
  companyId,
  onBudgetSaved,
}) => {
  const dispatch = useDispatch();

  const [step, setStep] = useState<
    "selectFile" | "selectSheet" | "detectBudget" | "updateGrid" | "saveBudget"
  >("selectFile");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [initialFileData, setInitialFileData] = useState<ParsedFileData | null>(
    null
  );
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [sheetPreviews, setSheetPreviews] = useState<
    Record<string, ParsedFileData>
  >({});
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(
    null
  );
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [descriptionColumn, setDescriptionColumn] = useState<number | null>(
    null
  );
  const [costColumn, setCostColumn] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUserInteractedWithGrid, setHasUserInteractedWithGrid] =
    useState(false);
  const [hiddenSheets, setHiddenSheets] = useState<Set<string>>(new Set());
  const [showManualProcessDialog, setShowManualProcessDialog] = useState(false);
  const [manualProcessFileName, setManualProcessFileName] =
    useState<string>("");

  const allowedTypes = [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/pdf",
  ];

  const supportedTypes = [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ];

  // Helper function to convert column names like "Col_1" to numeric indices
  const convertColumnNameToIndex = (columnName: string): number | null => {
    if (!columnName) return null;
    const match = columnName.match(/Col_(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  const notifyError = (message: string) =>
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });

  const notifySuccess = (message: string) => {
    toast.success(message, {
      position: "top-right",
      autoClose: 1500,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  };

  const handleFileUpload = async (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    if (!allowedTypes.includes(file.type)) {
      notifyError("Please select a valid file (CSV, Excel, or PDF)");
      return;
    }

    // Check if file type is not CSV or Excel (i.e., requires manual processing)
    if (!supportedTypes.includes(file.type)) {
      setIsProcessing(true);
      setUploadedFile(file);

      try {
        // Send the file to the backend for S3 storage
        await (dispatch as any)(
          extractBudget(projectUuid, companyId, file, null)
        );

        // Show the manual processing dialog after successful upload
        setManualProcessFileName(file.name);
        setShowManualProcessDialog(true);
        notifySuccess("File uploaded successfully for manual processing");
      } catch (error) {
        console.error("Error uploading file for manual processing:", error);
        notifyError("Failed to upload file. Please try again.");
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    setIsProcessing(true);
    setUploadedFile(file);

    try {
      // For Excel files, get sheet names and proceed to sheet selection
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        const sheets = await getExcelSheetNames(file);

        if (sheets.length > 1) {
          // Multiple sheets - load previews for all sheets
          const previews = await loadSheetPreviews(file, sheets);

          // Check how many non-empty sheets there are
          const nonEmptySheets = sheets.filter((sheetName) => {
            const previewData = previews[sheetName];
            if (!previewData) return true; // If we don't have preview data, assume it's not empty

            const isEmpty =
              previewData.data.length === 1 &&
              previewData.data[0].length === 1 &&
              previewData.data[0][0] === "[EMPTY]";
            return !isEmpty;
          });

          if (nonEmptySheets.length === 1) {
            // Only one non-empty sheet - auto-select it
            const selectedSheetName = nonEmptySheets[0];
            const parsedData = previews[selectedSheetName];
            setInitialFileData(parsedData);
            setSelectedSheet(selectedSheetName);
            setStep("selectSheet"); // Move to step 2 even though auto-selecting
            notifySuccess(
              `File loaded! Auto-selected the only non-empty sheet: "${selectedSheetName}".`
            );

            // Auto-advance to detection step
            setTimeout(() => {
              setStep("detectBudget");
              // Auto-start detection
              handleBudgetDetectionAuto(file, parsedData, selectedSheetName);
            }, 500);
          } else {
            // Multiple non-empty sheets - show selection
            setStep("selectSheet");
            notifySuccess(
              `File loaded! Found ${sheets.length} sheets (${nonEmptySheets.length} non-empty). Please select one to continue.`
            );
          }
        } else {
          // Single sheet - parse it, auto-select it, and start budget detection automatically
          const parsedData = await parseFile(file);
          setInitialFileData(parsedData);
          setSelectedSheet(sheets[0]);
          setStep("selectSheet"); // Move to step 2 even though auto-selecting
          notifySuccess("File loaded! Auto-selected single sheet.");

          // Auto-advance to detection step
          setTimeout(() => {
            setStep("detectBudget");
            // Auto-start detection
            handleBudgetDetectionAuto(file, parsedData, sheets[0]);
          }, 500);
        }
      } else if (file.type === "text/csv") {
        // CSV files don't have sheets - auto-select and start detection
        const parsedData = await parseFile(file);
        setInitialFileData(parsedData);
        setStep("selectSheet"); // Move to step 2 even though auto-selecting
        notifySuccess("CSV file loaded!");

        // Auto-advance to detection step
        setTimeout(() => {
          setStep("detectBudget");
          // Auto-start detection (no sheet name for CSV)
          handleBudgetDetectionAuto(file, parsedData, null);
        }, 500);
      } else if (file.type === "application/pdf") {
        // PDF files can't be parsed locally
        setInitialFileData({
          data: [],
          columns: [],
          fileName: file.name,
        });
        setStep("selectSheet"); // Move to step 2 even though auto-selecting
        notifySuccess("PDF file loaded!");

        // Auto-advance to detection step
        setTimeout(() => {
          setStep("detectBudget");
          // Auto-start detection (no sheet name for PDF)
          handleBudgetDetectionAuto(file, null, null);
        }, 500);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.error("Error processing file:", error);

      if (errorMessage.includes("empty")) {
        notifyError(
          "The selected file or sheet appears to be empty. Please select a file with data."
        );
      } else {
        notifyError("Error processing file: " + errorMessage);
      }
      setStep("selectFile");
    } finally {
      setIsProcessing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    multiple: false,
    accept: {
      "text/csv": [".csv"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
        ".xls",
      ],
      "application/pdf": [".pdf"],
    },
  });

  const handleRowHeaderClick = useCallback(
    (rowIdx: number) => {
      // Mark that user has interacted with the grid (Step 4 activity)
      if (!hasUserInteractedWithGrid) {
        setHasUserInteractedWithGrid(true);
      }

      setSelectedRows((prev) => {
        if (prev.includes(rowIdx)) {
          return prev.filter((r) => r !== rowIdx);
        } else {
          return [...prev, rowIdx];
        }
      });
    },
    [hasUserInteractedWithGrid]
  );

  const handleSelectedRowsChange = useCallback((newSelectedRows: number[]) => {
    setSelectedRows(newSelectedRows);
  }, []);

  const handleColumnHeaderClick = useCallback(
    (colIdx: number, event: React.MouseEvent) => {
      // Mark that user has interacted with the grid (Step 4 activity)
      if (!hasUserInteractedWithGrid) {
        setHasUserInteractedWithGrid(true);
      }

      if (event.shiftKey) {
        // Shift+Click for cost column
        setCostColumn((prev) => (prev === colIdx ? null : colIdx));
      } else {
        // Regular click for description column
        setDescriptionColumn((prev) => (prev === colIdx ? null : colIdx));
      }
    },
    [hasUserInteractedWithGrid]
  );

  const loadSheetPreviews = async (file: File, sheetNames: string[]) => {
    const previews: Record<string, ParsedFileData> = {};
    const emptySheets: string[] = [];

    for (const sheetName of sheetNames) {
      try {
        const parsedData = await parseFile(file, sheetName);
        previews[sheetName] = parsedData;

        // Check if sheet is empty
        const isEmpty =
          parsedData.data.length === 1 &&
          parsedData.data[0].length === 1 &&
          parsedData.data[0][0] === "[EMPTY]";

        if (isEmpty) {
          emptySheets.push(sheetName);
        }
      } catch (error) {
        console.warn(`Failed to load preview for sheet "${sheetName}":`, error);
        // Create a minimal preview for failed sheets
        previews[sheetName] = {
          data: [["[ERROR]"]],
          columns: ["A"],
          fileName: file.name,
          sheets: sheetNames,
          selectedSheet: sheetName,
        };
      }
    }

    setSheetPreviews(previews);

    // Automatically hide empty sheets
    if (emptySheets.length > 0) {
      setHiddenSheets(new Set(emptySheets));
    }

    // Return the previews for immediate use
    return previews;
  };

  const handleSheetSelection = async (sheetName: string) => {
    if (!uploadedFile) return;

    setIsProcessing(true);
    try {
      // Use cached preview data if available, otherwise parse the sheet
      let parsedData: ParsedFileData;
      if (sheetPreviews[sheetName]) {
        parsedData = sheetPreviews[sheetName];
      } else {
        parsedData = await parseFile(uploadedFile, sheetName);
      }

      setInitialFileData(parsedData);
      setSelectedSheet(sheetName);
      setStep("detectBudget");
      notifySuccess(`Sheet "${sheetName}" selected!`);

      // Auto-start budget detection
      setTimeout(() => {
        handleBudgetDetectionAuto(uploadedFile, parsedData, sheetName);
      }, 500);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.error("Error parsing selected sheet:", error);

      if (errorMessage.includes("empty")) {
        notifyError(
          `Sheet "${sheetName}" appears to be empty. Please select a different sheet or file with data.`
        );
      } else {
        notifyError("Error parsing selected sheet: " + errorMessage);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleHideSheet = (sheetName: string) => {
    setHiddenSheets((prev) => new Set([...prev, sheetName]));
    notifySuccess(`Sheet "${sheetName}" hidden`);
  };

  const handleBudgetDetectionAuto = async (
    file: File | null,
    parsedData: ParsedFileData | null,
    sheetName?: string | null
  ) => {
    if (!file) {
      file = uploadedFile;
    }
    if (!file) return;

    // Use the provided sheet name, or fall back to the state
    const effectiveSheetName =
      sheetName !== undefined ? sheetName : selectedSheet;

    setIsProcessing(true);
    try {
      // Call API to extract budget items, passing the selected sheet name for Excel files
      console.log("Calling extractBudget with sheet name:", effectiveSheetName);
      const result = await dispatch(
        extractBudget(projectUuid, companyId, file, effectiveSheetName) as any
      );

      if (result.success && result.data) {
        setExtractedData(result.data as ExtractedData);

        // Initialize with API-detected columns and pre-select budget item rows
        const descColIndex = convertColumnNameToIndex(
          result.data.description_column
        );
        const costColIndex = convertColumnNameToIndex(result.data.cost_column);

        if (descColIndex !== null) {
          setDescriptionColumn(descColIndex);
        }
        if (costColIndex !== null) {
          setCostColumn(costColIndex);
        }

        // Pre-select rows that contain budget items
        if (result.data.budget_items && result.data.budget_items.length > 0) {
          const budgetRowIndices = result.data.budget_items.map((item: any) => {
            const rowNumber = parseInt(item.row.replace("Row_", ""));
            return rowNumber - 1; // Convert to 0-based index for the grid
          });
          setSelectedRows(budgetRowIndices);
        }

        setStep("updateGrid");
        notifySuccess(
          `Budget items detected! Found ${
            result.data.budget_items?.length || 0
          } items. You can now review and adjust in the grid.`
        );
      } else {
        notifyError(result.message || "Failed to detect budget items");
        // Still proceed to update grid step to allow manual selection
        setStep("updateGrid");
      }
    } catch (error) {
      notifyError("Error detecting budget items");
      console.error("Error detecting budget items:", error);
      // Still proceed to update grid step to allow manual selection
      setStep("updateGrid");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInitiateSave = async (
    selectedRowIndices: number[],
    descCol: number | null,
    costCol: number | null,
    data: any[][]
  ) => {
    // Move to save budget step (Step 5)
    setStep("saveBudget");

    // Execute the actual save
    await handleSave(selectedRowIndices, descCol, costCol, data);
  };

  const handleSave = async (
    selectedRowIndices: number[],
    descCol: number | null,
    costCol: number | null,
    data: any[][]
  ) => {
    if (selectedRowIndices.length === 0) {
      notifyError("Please select at least one row");
      return;
    }

    if (descCol === null && costCol === null) {
      notifyError("Please select at least one column (description or cost)");
      return;
    }

    if (!data || data.length === 0) {
      notifyError("No data available to save");
      return;
    }

    setIsSaving(true);
    try {
      // Process selected rows into budget items format
      const budgetItems = selectedRowIndices
        .filter((rowIdx) => rowIdx >= 0 && rowIdx < data.length) // Ensure valid row indices
        .map((rowIdx) => {
          const row = data[rowIdx];
          if (!row) {
            console.warn(`Row ${rowIdx} is undefined, skipping`);
            return null;
          }

          const description =
            descCol !== null ? String(row[descCol] || "").trim() : "";
          let amount = 0;

          if (costCol !== null && row[costCol]) {
            // Parse the cost string, removing $ and commas
            const costStr = String(row[costCol]).replace(/[$,\s]/g, "");
            amount = parseFloat(costStr) || 0;
          }

          // Create budget item matching backend BdgtLnItmInput interface
          return {
            bdgtCategory: "", // Optional: Category can be blank
            bdgtDesc: description, // Required: Budget description
            amt: amount, // Required: Amount/cost value
            pId: projectUuid, // Required: Project ID
            cId: companyId, // Required: Company ID
          };
        })
        .filter((item) => {
          if (item === null) return false;

          // Check if description is valid (not empty and not "[EMPTY]")
          const hasValidDescription =
            descCol !== null
              ? item.bdgtDesc &&
                item.bdgtDesc.length > 0 &&
                item.bdgtDesc !== "[EMPTY]"
              : true; // If no description column selected, consider it valid

          // Check if cost is valid (not zero)
          const hasValidCost = costCol !== null ? item.amt > 0 : true; // If no cost column selected, consider it valid

          // Item is valid only if BOTH description AND cost are valid (for their respective selected columns)
          return hasValidDescription && hasValidCost;
        });

      if (budgetItems.length === 0) {
        notifyError("No valid budget items found in selected rows");
        setIsSaving(false);
        return;
      }

      console.log(`Creating ${budgetItems.length} budget items:`, budgetItems);

      // Use the budget items directly - they already match the backend BdgtLnItmInput interface
      const modifiedItems = budgetItems;
      console.log("Final modifiedItems being sent to GraphQL:", modifiedItems);
      try {
        // Wait for delete to finish before creating new items
        const deleteResult = await new Promise((resolve) =>
          (dispatch as any)(
            deleteBudgets({ pId: projectUuid, cId: companyId }, (res: any) =>
              resolve(res)
            )
          )
        );
        // If delete indicates failure, stop here
        if (deleteResult && deleteResult.success === false) {
          const msg =
            deleteResult.message || "Failed to delete existing budgets";
          notifyError(msg);
          setIsSaving(false);
          setStep("updateGrid");
          return;
        }
      } catch (err) {
        console.error("Error deleting budgets:", err);
        notifyError("Failed to delete existing budgets");
        setIsSaving(false);
        setStep("updateGrid");
        return;
      }

      // Now create new budget items (wait for result if action uses a callback)

      await new Promise((resolve) =>
        (dispatch as any)(
          createBudgetItem({ items: modifiedItems }, (res: any) => {
            console.log("Save Budget Items Response:", res);

            // If res is undefined/null (GraphQL error), but we got here, treat as success in development
            if (!res && process.env.NODE_ENV === "development") {
              console.warn(
                "No response from CreateBudgetItems mutation - backend resolver missing"
              );
              console.info(
                "In development mode, treating as success since items were formatted correctly"
              );

              const itemCount = budgetItems.length;
              notifySuccess(`${itemCount} Budget Items Saved!`);

              // Wait for toast to be visible, then close and call callback
              setTimeout(() => {
                onBudgetSaved();
                onClose();
              }, 2000);

              setIsSaving(false);
              return;
            }

            if (res?.success !== false) {
              // Consider success if res is truthy and doesn't explicitly indicate failure
              const itemCount = budgetItems.length;
              notifySuccess(`${itemCount} Budget Items Saved!`);

              // Wait for toast to be visible, then close and call callback
              setTimeout(() => {
                onBudgetSaved();
                onClose();
              }, 2000);
            } else {
              const errorMessage =
                res?.message ||
                "Failed to save budget items! Backend resolver may be missing.";
              notifyError(errorMessage);
              console.error("Budget save error:", res);
              console.error("Possible causes:");
              console.error(
                "1. Backend GraphQL server missing CreateBudgetItems resolver"
              );
              console.error(
                "2. Backend server not accessible or authentication issue"
              );
              console.error("3. GraphQL schema mismatch");
              // Go back to update grid step on error
              setStep("updateGrid");
            }
            setIsSaving(false);
          })
        )
      );
    } catch (error) {
      notifyError("Error saving budget items");
      console.error("Error saving budget items:", error);
      setIsSaving(false);
      // Go back to update grid step on error
      setStep("updateGrid");
    }
  };

  const handleClose = () => {
    setStep("selectFile");
    setUploadedFile(null);
    setInitialFileData(null);
    setSelectedSheet(null);
    setSheetPreviews({});
    setExtractedData(null);
    setSelectedRows([]);
    setDescriptionColumn(null);
    setCostColumn(null);
    setHasUserInteractedWithGrid(false);
    onClose();
  };

  const renderSelectFileStep = () => (
    <>
      <div
        style={{
          padding: "30px 40px",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "300px",
          justifyContent: "center",
        }}
      >
        <h3 style={{ margin: "0 0 15px 0", fontSize: "24px", color: "#333" }}>
          Select Budget File&nbsp;&nbsp;(Step 1 of 5)
        </h3>
        <p style={{ margin: "0 0 30px 0", color: "#666", fontSize: "16px" }}>
          Select a file (Excel, CSV, or PDF) that contains a construction
          budget.
        </p>

        <div
          {...getRootProps()}
          style={{
            border: "2px dashed #ccc",
            borderRadius: "12px",
            padding: "50px 40px",
            textAlign: "center" as const,
            cursor: "pointer",
            margin: "0 0 20px 0",
            backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
            borderColor: isDragActive ? "#2196f3" : "#ccc",
            transition: "all 0.2s ease",
            width: "100%",
            maxWidth: "500px",
          }}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <p style={{ margin: 0, fontSize: "18px", color: "#2196f3" }}>
              Drop the file here...
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: "16px", color: "#666" }}>
              Drag & drop a budget file here, or click to select a file
            </p>
          )}
        </div>

        <p style={{ fontSize: "14px", color: "#999", margin: "0 0 20px 0" }}>
          Accepted formats: .pdf, .csv, .xls, .xlsx
        </p>

        {isProcessing && (
          <div
            style={{
              marginTop: "20px",
              padding: "15px",
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid #2196f3",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ margin: 0, color: "#666" }}>
              Processing budget file...
            </p>
          </div>
        )}
      </div>
    </>
  );

  const renderSheetPreviewWithGrid = (
    sheetName: string,
    previewData: ParsedFileData
  ) => {
    const isEmpty =
      previewData.data.length === 1 &&
      previewData.data[0].length === 1 &&
      previewData.data[0][0] === "[EMPTY]";

    const hasError =
      previewData.data.length === 1 &&
      previewData.data[0].length === 1 &&
      previewData.data[0][0] === "[ERROR]";

    return (
      <div
        key={sheetName}
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "15px",
          backgroundColor: "#fff",
          marginBottom: "15px",
        }}
      >
        {/* Sheet header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
            paddingBottom: "10px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "16px",
              color: "#333",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {sheetName}
            {isEmpty && (
              <span style={{ color: "#ff9800", fontSize: "14px" }}>
                ⚠️ Empty
              </span>
            )}
            {hasError && (
              <span style={{ color: "#f44336", fontSize: "14px" }}>
                ❌ Error
              </span>
            )}
          </h4>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              onClick={() => handleSheetSelection(sheetName)}
              className="btn-primary"
              disabled={isProcessing}
              style={{ fontSize: "14px", padding: "6px 12px" }}
            >
              Select This Sheet
            </Button>
            <Button
              onClick={() => handleHideSheet(sheetName)}
              className="btn-secondary"
              disabled={isProcessing}
              style={{ fontSize: "14px", padding: "6px 12px" }}
            >
              Hide Sheet
            </Button>
          </div>
        </div>

        {/* Sheet info */}
        <div
          style={{
            fontSize: "14px",
            color: "#666",
            marginBottom: "10px",
            display: "flex",
            gap: "20px",
          }}
        >
          <span>
            <strong>Rows:</strong> {previewData.data.length}
          </span>
          <span>
            <strong>Columns:</strong> {previewData.columns.length}
          </span>
        </div>

        {/* Preview grid or warning */}
        {isEmpty ? (
          <div
            style={{
              padding: "20px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "4px",
              textAlign: "center",
              color: "#856404",
            }}
          >
            This sheet appears to be empty and contains no data.
          </div>
        ) : hasError ? (
          <div
            style={{
              padding: "20px",
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              textAlign: "center",
              color: "#721c24",
            }}
          >
            Error loading this sheet. It may be corrupted or contain unsupported
            content.
          </div>
        ) : (
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              overflow: "hidden",
              backgroundColor: "#fafafa",
            }}
          >
            {/* Grid header */}
            <div
              style={{
                display: "flex",
                backgroundColor: "#f5f5f5",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <div
                style={{
                  width: "40px",
                  padding: "8px 4px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  textAlign: "center",
                  borderRight: "1px solid #e0e0e0",
                }}
              ></div>
              {previewData.columns
                .slice(0, Math.min(6, previewData.columns.length))
                .map((col, idx) => (
                  <div
                    key={col}
                    style={{
                      minWidth: "80px",
                      maxWidth: "120px",
                      padding: "8px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      textAlign: "center",
                      borderRight:
                        idx < Math.min(5, previewData.columns.length - 1)
                          ? "1px solid #e0e0e0"
                          : "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {col}
                  </div>
                ))}
              {previewData.columns.length > 6 && (
                <div
                  style={{
                    padding: "8px",
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  +{previewData.columns.length - 6} more
                </div>
              )}
            </div>

            {/* Grid data - show first 4 rows */}
            {previewData.data
              .slice(0, Math.min(4, previewData.data.length))
              .map((row, rowIdx) => (
                <div
                  key={rowIdx}
                  style={{
                    display: "flex",
                    backgroundColor: rowIdx % 2 === 0 ? "#fff" : "#f9f9f9",
                    borderBottom:
                      rowIdx < Math.min(3, previewData.data.length - 1)
                        ? "1px solid #e0e0e0"
                        : "none",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      padding: "8px 4px",
                      fontSize: "12px",
                      textAlign: "center",
                      borderRight: "1px solid #e0e0e0",
                      color: "#666",
                    }}
                  >
                    {rowIdx + 1}
                  </div>
                  {row
                    .slice(0, Math.min(6, row.length))
                    .map((cell, cellIdx) => (
                      <div
                        key={cellIdx}
                        style={{
                          minWidth: "80px",
                          maxWidth: "120px",
                          padding: "8px",
                          fontSize: "12px",
                          borderRight:
                            cellIdx < Math.min(5, row.length - 1)
                              ? "1px solid #e0e0e0"
                              : "none",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cell === "[EMPTY]" ? (
                          <span style={{ color: "#ccc", fontStyle: "italic" }}>
                            empty
                          </span>
                        ) : (
                          String(cell)
                        )}
                      </div>
                    ))}
                  {row.length > 6 && (
                    <div
                      style={{
                        padding: "8px",
                        fontSize: "12px",
                        color: "#666",
                        fontStyle: "italic",
                      }}
                    >
                      ...
                    </div>
                  )}
                </div>
              ))}

            {previewData.data.length > 4 && (
              <div
                style={{
                  padding: "8px",
                  textAlign: "center",
                  fontSize: "12px",
                  color: "#666",
                  fontStyle: "italic",
                  backgroundColor: "#f5f5f5",
                }}
              >
                +{previewData.data.length - 4} more rows
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSelectSheetStep = () => {
    const availableSheets = Object.keys(sheetPreviews);
    if (availableSheets.length <= 1) return null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "85vh",
        }}
      >
        {/* Header section */}
        <div
          style={{
            padding: "12px 30px",
            // borderBottom: '1px solid #e0e0e0',
            backgroundColor: "#f9f9f9",
            flexShrink: 0,
          }}
        >
          <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#333" }}>
            Select Sheet&nbsp;&nbsp;(Step 2 of 5)
          </h3>
          <p style={{ margin: "0", color: "#666", fontSize: "14px" }}>
            Your Excel file contains {availableSheets.length} sheets
            {hiddenSheets.size > 0 &&
              ` (${availableSheets.length - hiddenSheets.size} visible, ${
                hiddenSheets.size
              } hidden)`}
            . Please select the sheet that contains your budget data.
          </p>
        </div>

        {/* Sheets preview section with grid previews */}
        <div
          style={{
            flex: 1,
            padding: "20px 30px",
            overflow: "auto",
            minHeight: 0,
          }}
        >
          {availableSheets
            .filter((sheetName) => !hiddenSheets.has(sheetName))
            .map((sheetName) => {
              const previewData = sheetPreviews[sheetName];
              return renderSheetPreviewWithGrid(sheetName, previewData);
            })}
        </div>

        {isProcessing && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              padding: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "8px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid #2196f3",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ margin: 0, color: "#666" }}>
              Loading selected sheet...
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderDetectBudgetStep = () => {
    // Use initial file data if available for preview
    const displayData = initialFileData;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "85vh",
        }}
      >
        {/* Header section */}
        <div
          style={{
            padding: "12px 30px",
            // borderBottom: '1px solid #e0e0e0',
            backgroundColor: "#f9f9f9",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <div style={{ flex: 1 }}>
              <h3
                style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#333" }}
              >
                Detect Budget&nbsp;&nbsp;(Step 3 of 5)
              </h3>
              <div style={{ fontSize: "14px", color: "#666" }}>
                {displayData && (
                  <>
                    <div>
                      <strong>File:</strong> {displayData.fileName}
                    </div>
                    {selectedSheet && (
                      <div>
                        <strong>Sheet:</strong> {selectedSheet}
                      </div>
                    )}
                    <div style={{ marginTop: "5px" }}>
                      <span style={{ color: "#2196f3" }}>
                        ⏳ Analyzing {displayData.data.length} rows and
                        automatically detecting budget items...
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Horizontal rule separator */}
        <div style={{ borderTop: "1px solid #e0e0e0" }} />

        {/* Sheet preview grid */}
        {displayData &&
          displayData.data.length > 0 &&
          !(
            displayData.data.length === 1 &&
            displayData.data[0].length === 1 &&
            displayData.data[0][0] === "[EMPTY]"
          ) && (
            <div
              style={{
                flex: 1,
                padding: "20px 30px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  marginBottom: "10px",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                <strong>Preview of Selected Sheet:</strong>
              </div>
              <BudgetGrid
                excelData={displayData}
                budgetGrid={undefined}
                selectedRows={[]}
                descriptionColumn={null}
                costColumn={null}
                maxRows={500}
                isSaving={false}
              />
            </div>
          )}

        {isProcessing && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              padding: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "8px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid #2196f3",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ margin: 0, color: "#666" }}>
              Analyzing file and detecting budget items...
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderUpdateGridStep = () => {
    // Use initial file data if available, otherwise fall back to extracted data
    const displayData =
      initialFileData ||
      (extractedData?.excel_data
        ? {
            data: extractedData.excel_data.data,
            columns: extractedData.excel_data.columns,
            fileName: extractedData.project_name || "Unknown",
          }
        : null);

    if (!displayData) return null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "85vh",
        }}
      >
        {/* Header section */}
        <div
          style={{
            padding: "12px 30px",
            // borderBottom: '1px solid #e0e0e0',
            backgroundColor: "#f9f9f9",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "30px",
            }}
          >
            {/* Left Column - Title and File Info */}
            <div style={{ flex: 1 }}>
              <h3
                style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#333" }}
              >
                Update Grid&nbsp;&nbsp;(Step 4 of 5)
              </h3>
              <div style={{ fontSize: "14px", color: "#666" }}>
                Using the instructions below, review and adjust the detected
                budget items as needed.
                <br />
                When ready, click "Save Budget Items" to finalize.
              </div>
            </div>
            <div
              style={{
                flex: 1,
                border: "none",
                fontSize: "14px",
                color: "#666",
                lineHeight: "1.6",
              }}
            >
              <div
                style={{ fontSize: "14px", color: "#666", lineHeight: "1.4" }}
              >
                <div>
                  <strong>File:</strong> {displayData.fileName}
                </div>
                {selectedSheet && (
                  <div>
                    <strong>Sheet:</strong> {selectedSheet}
                  </div>
                )}
                {/* <div style={{ marginTop: '5px' }}>
                  {extractedData ? (
                    <span style={{ color: '#228B22' }}>
                      ✓ {extractedData.budget_items?.length || 0} budget items detected automatically. Click on the grid to review and adjust.
                    </span>
                  ) : (
                    <span style={{ color: '#ff9800' }}>
                      ⚠️ Manual selection required - click row headers and column headers to select budget items
                    </span>
                  )}
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Horizontal rule separator */}
        <div style={{ borderTop: "1px solid #e0e0e0" }} />

        {/* BudgetGrid section */}
        <div
          style={{
            flex: 1,
            padding: "10px 30px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <BudgetGrid
            excelData={displayData}
            budgetGrid={undefined}
            apiResponse={
              extractedData
                ? {
                    status: extractedData.status,
                    budget_items: extractedData.budget_items,
                    description_column:
                      convertColumnNameToIndex(
                        extractedData.description_column
                      ) || undefined,
                    cost_column:
                      convertColumnNameToIndex(extractedData.cost_column) ||
                      undefined,
                  }
                : undefined
            }
            selectedRows={selectedRows}
            descriptionColumn={descriptionColumn}
            costColumn={costColumn}
            onRowHeaderClick={handleRowHeaderClick}
            onColumnHeaderClick={handleColumnHeaderClick}
            onSelectedRowsChange={handleSelectedRowsChange}
            onSave={handleInitiateSave}
            maxRows={500}
            isSaving={false}
          />
        </div>
      </div>
    );
  };

  const renderSaveBudgetStep = () => {
    // Use initial file data if available, otherwise fall back to extracted data
    const displayData =
      initialFileData ||
      (extractedData?.excel_data
        ? {
            data: extractedData.excel_data.data,
            columns: extractedData.excel_data.columns,
            fileName: extractedData.project_name || "Unknown",
          }
        : null);

    if (!displayData) return null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "85vh",
        }}
      >
        {/* Header section */}
        <div
          style={{
            padding: "12px 30px",
            // borderBottom: '1px solid #e0e0e0',
            backgroundColor: "#f9f9f9",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "20px",
            }}
          >
            <div style={{ flex: 1 }}>
              <h3
                style={{ margin: "0 0 8px 0", fontSize: "18px", color: "#333" }}
              >
                Save Budget&nbsp;&nbsp;(Step 5 of 5)
              </h3>
              <div
                style={{ fontSize: "14px", color: "#666", lineHeight: "1.4" }}
              >
                <div>
                  <strong>File:</strong> {displayData.fileName}
                </div>
                {selectedSheet && (
                  <div>
                    <strong>Sheet:</strong> {selectedSheet}
                  </div>
                )}
                <div style={{ marginTop: "5px" }}>
                  <span style={{ color: "#2196f3" }}>
                    ⏳ Saving budget items to database...
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Horizontal rule separator */}
        <div style={{ borderTop: "1px solid #e0e0e0" }} />

        {/* BudgetGrid section */}
        <div
          style={{
            flex: 1,
            padding: "20px 30px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <BudgetGrid
            excelData={displayData}
            budgetGrid={undefined}
            apiResponse={
              extractedData
                ? {
                    status: extractedData.status,
                    budget_items: extractedData.budget_items,
                    description_column:
                      convertColumnNameToIndex(
                        extractedData.description_column
                      ) || undefined,
                    cost_column:
                      convertColumnNameToIndex(extractedData.cost_column) ||
                      undefined,
                  }
                : undefined
            }
            selectedRows={selectedRows}
            descriptionColumn={descriptionColumn}
            costColumn={costColumn}
            maxRows={500}
            isSaving={true}
          />
        </div>

        {isSaving && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              padding: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              // borderRadius: '8px',
              // boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              display: "flex",
              alignItems: "center",
              gap: "10px",
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                border: "2px solid #2196f3",
                borderTop: "2px solid transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ margin: 0, color: "#666" }}>Saving budget items...</p>
          </div>
        )}
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (step) {
      case "selectFile":
        return renderSelectFileStep();
      case "selectSheet":
        return renderSelectSheetStep();
      case "detectBudget":
        return renderDetectBudgetStep();
      case "updateGrid":
        return renderUpdateGridStep();
      case "saveBudget":
        return renderSaveBudgetStep();
      default:
        return renderSelectFileStep();
    }
  };

  const isWideStep =
    step === "updateGrid" ||
    step === "saveBudget" ||
    step === "selectSheet" ||
    step === "detectBudget";

  return (
    <>
      <BudgetExtractorModal
        open={open}
        onClose={handleClose}
        header={<StepIndicator currentStep={step} />}
        hideCloseIcon={false}
        footer={null}
      >
        <div
          style={{
            width: isWideStep ? "95vw" : "auto",
            maxWidth: isWideStep ? "1400px" : "800px",
            minWidth: isWideStep ? "800px" : "600px",
            height: isWideStep ? "85vh" : "auto",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {renderCurrentStep()}
        </div>
      </BudgetExtractorModal>

      {/* Manual Processing Dialog */}
      <BudgetExtractorModal
        open={showManualProcessDialog}
        onClose={() => {
          setShowManualProcessDialog(false);
          onClose();
        }}
        header="Manual Processing Required"
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "10px 20px",
            }}
          >
            <Button
              onClick={() => {
                setShowManualProcessDialog(false);
                onClose();
              }}
              className="btn-primary"
            >
              OK
            </Button>
          </div>
        }
      >
        <div style={{ padding: "20px 30px" }}>
          <p
            style={{
              margin: "0 0 15px 0",
              fontSize: "16px",
              lineHeight: "1.5",
            }}
          >
            The file <strong>{manualProcessFileName}</strong> is not a CSV or
            Excel file and cannot be processed automatically.
          </p>
          <p style={{ margin: "0", fontSize: "16px", lineHeight: "1.5" }}>
            This file will need to be processed manually by our team. We will
            email you the Budget report when complete (usually within 2 business
            days).
          </p>
        </div>
      </BudgetExtractorModal>
    </>
  );
};
